import { HttpErrorResponse } from '@angular/common/http';
import { Component, Inject, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { BehaviorSubject, Observable, Subscription, combineLatest, of } from 'rxjs';
import { catchError, distinctUntilChanged, filter, map, switchMap, tap } from 'rxjs/operators';
import { ApiErrorFactory } from '../../../core/api-errors';
import { getImageSrc } from '../../../core/image-utilities';
import { GetAppConfigResponse, ProductSettingsService } from '../../../core/product-settings';
import { Site, SiteService } from '../../../core/site';
import { Template, TemplateApiService, TemplateLookupResponse } from '../../../core/template';
import { TemplateLookupMessage } from '../../../core/template/template';
import { IsPartner, UserService } from '../../../core/user.service';
import { ecommerceTemplates, vcor, vwct } from '../../create-from-template/create-from-template.constants';

enum LIMITS {
  THREE_COLUMNS = 1300,
  TWO_COLUMNS = 900,
}

@Component({
  selector: 'app-staging-from-template-modal',
  templateUrl: './staging-from-template-modal.component.html',
  styleUrls: ['./staging-from-template-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class StagingFromTemplateModalComponent implements OnInit {
  starImage: string;
  columns: number;
  rowHeight: number;
  partnerId: string;
  siteId: string;
  templateLookupPartnerIds = [vcor];

  protected marketingTemplates$$: BehaviorSubject<Template[]> = new BehaviorSubject(undefined);
  readonly marketingTemplates$: Observable<Template[]> = this.marketingTemplates$$.asObservable();

  protected partnerTemplates$$: BehaviorSubject<Template[]> = new BehaviorSubject(undefined);
  readonly partnerTemplates$: Observable<Template[]> = this.partnerTemplates$$.asObservable();

  private easyEcommerceTemplates$$: BehaviorSubject<Template[]> = new BehaviorSubject(undefined);
  readonly easyEcommerceTemplates$: Observable<Template[]> = this.easyEcommerceTemplates$$.asObservable();

  private standardTemplates$$: BehaviorSubject<Template[]> = new BehaviorSubject(undefined);
  readonly standardTemplates$: Observable<Template[]> = this.standardTemplates$$.asObservable();

  private templateLoading$$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  readonly loading$: Observable<boolean> = this.templateLoading$$.asObservable();
  private hasVisibleTemplates$$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  readonly hasVisibleTemplates$: Observable<boolean> = this.hasVisibleTemplates$$.asObservable();

  protected subscriptions: Subscription[] = [];

  showIncludedTemplates$: Observable<boolean>;
  private isSuperAdmin = false;
  private isSmbUser = false;

  constructor(
    protected alertService: SnackbarService,
    protected apiErrorFactory: ApiErrorFactory,
    protected siteService: SiteService,
    protected userService: UserService,
    public dialogRef: MatDialogRef<StagingFromTemplateModalComponent>,
    private templateService: TemplateApiService,
    private translateService: TranslateService,
    protected productSettingsService: ProductSettingsService,
    @Inject(MAT_DIALOG_DATA) public data: any,
  ) {
    this.rowHeight = 550;
    this.starImage = getImageSrc('/images/star-yellow.svg');
  }

  ngOnInit(): void {
    this.calcColumns(window.innerWidth);
    const partnerId$ = this.siteService.get().pipe(
      map((site: Site) => site.partnerId),
      distinctUntilChanged(),
    );
    this.showIncludedTemplates$ = partnerId$.pipe(
      switchMap((partnerId) => this.productSettingsService.getAppConfig(partnerId)),
      map((conf: GetAppConfigResponse) => !!conf && !!conf.config && !!conf.config.showIncludedTemplates),
      catchError(() => {
        return of(true);
      }),
    );
    combineLatest([partnerId$, this.userService.isSuperAdmin$, this.userService.isPartner$])
      .pipe(
        tap(([partnerId, isSuperAdmin, isPartner]) => {
          this.partnerId = partnerId;
          this.isSuperAdmin = isSuperAdmin;
          this.isSmbUser = isPartner === IsPartner.No;
          this.templateLookupPartnerIds = [vcor, partnerId];
          if (this.isSuperAdmin) {
            this.templateLookupPartnerIds.push(vwct);
          }
        }),
        switchMap(() => this.lookupTemplates(this.templateLookupPartnerIds, false)),
        filter((templateResponse) => !!templateResponse),
        map((templateResponse) => templateResponse.templates),
      )
      .subscribe((templateList) => {
        const filteredTemplates = this.isSmbUser ? templateList.filter((t) => t.isTemplateDistributed) : templateList;
        const marketingTemplates = filteredTemplates.filter((t) => t.partnerId === vwct);
        const partnerTemplates = filteredTemplates.filter((t) => t.partnerId !== vcor && t.partnerId !== vwct);
        const standardTemplates = filteredTemplates.filter(
          (t) => t.partnerId === vcor && !ecommerceTemplates.has(t.siteId),
        );
        const easyEcommerceTemplates = filteredTemplates.filter((t) => ecommerceTemplates.has(t.siteId));
        this.marketingTemplates$$.next(marketingTemplates);
        this.partnerTemplates$$.next(partnerTemplates);
        this.standardTemplates$$.next(standardTemplates);
        this.easyEcommerceTemplates$$.next(easyEcommerceTemplates);
        this.hasVisibleTemplates$$.next(filteredTemplates.length > 0);
        this.templateLoading$$.next(false);
      });
  }

  onResize(event: any): void {
    this.calcColumns(event.target.innerWidth);
  }

  selectTemplate(template: Template): void {
    this.dialogRef.close(template.siteId);
  }

  calcColumns(width: number): void {
    if (width >= LIMITS.THREE_COLUMNS) {
      this.columns = 3;
    } else if (width >= LIMITS.TWO_COLUMNS) {
      this.columns = 2;
    } else {
      this.columns = 1;
    }
  }

  // Do not use independently from setTemplateSites
  private lookupTemplates(partnerIds: string[], includeUnpublished: boolean): Observable<TemplateLookupResponse> {
    const lookupMessage = new TemplateLookupMessage(partnerIds, includeUnpublished, '', false);
    return this.templateService.lookup(lookupMessage).pipe(
      catchError((err) => {
        this.handleError(err);
        return of(err);
      }),
    );
  }

  handleError(errorResponse: HttpErrorResponse): void {
    const parsedError = this.apiErrorFactory.parseErrorResponse(errorResponse);
    this.displayError(parsedError.getUserMessage());
  }

  displayError(error: string): void {
    this.alertService.openErrorSnack(error, this.translateService.instant('ALERTS.GENERAL.DISMISS'));
  }
}
